import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Plus, Search, Upload, Camera, Trash2, Edit2, Loader2,
  AlertTriangle, Package, X, ChevronDown, History, ArrowUpCircle, ArrowDownCircle, FileText
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { DashboardLayout } from "./Dashboard";
import apiClient from "../../lib/api";
import { toastApiError } from "../../lib/toastApiError";

const PharmacyInventory = () => {
  const { t, i18n } = useTranslation("pharmacy");
  const dateLocale = i18n.language === "en" ? "en-US" : "fr-FR";
  const [inventory, setInventory] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");

  // Add Dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [quantity, setQuantity] = useState("");
  const [price, setPrice] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [alertThreshold, setAlertThreshold] = useState("10");
  const [expiryDate, setExpiryDate] = useState("");
  const [addLoading, setAddLoading] = useState(false);
  
  // Manual medicine entry
  const [addMode, setAddMode] = useState("search"); // "search" or "manual"
  const [medicineForms, setMedicineForms] = useState([]);
  const [manualMedicine, setManualMedicine] = useState({
    name: "",
    brand: "",
    molecule: "",
    dosage: "",
    form: "",
    requires_prescription: false
  });

  // Edit Dialog
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editQuantity, setEditQuantity] = useState("");
  const [editPrice, setEditPrice] = useState("");
  const [editPurchasePrice, setEditPurchasePrice] = useState("");
  const [editBrand, setEditBrand] = useState("");
  const [batches, setBatches] = useState({});
  const [loadingBatches, setLoadingBatches] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  
  // Stock movement
  const [stockAction, setStockAction] = useState("add"); // "add" or "remove"
  const [stockMovementQty, setStockMovementQty] = useState("");
  const [stockMovementDate, setStockMovementDate] = useState(new Date().toISOString().split('T')[0]);
  const [stockMovementNote, setStockMovementNote] = useState("");
  const [stockMovementExpiry, setStockMovementExpiry] = useState("");
  const [stockHistory, setStockHistory] = useState([]);

  // OCR Dialog
  const [showOCRDialog, setShowOCRDialog] = useState(false);
  const [ocrLoading, setOCRLoading] = useState(false);
  const [ocrResults, setOCRResults] = useState([]);
  const fileInputRef = useRef(null);

  const toggleBatches = async (itemId) => {
    if (batches[itemId]) {
      setBatches(prev => { const n = {...prev}; delete n[itemId]; return n; });
      return;
    }
    setLoadingBatches(prev => ({...prev, [itemId]: true}));
    try {
      const res = await apiClient.get(`/pharmacy/inventory/${itemId}/batches`);
      setBatches(prev => ({...prev, [itemId]: res.data.batches || []}));
    } catch { /* ignore */ }
    setLoadingBatches(prev => ({...prev, [itemId]: false}));
  };


  // Medicine Search
  const [medicineSearch, setMedicineSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  // Stock History Dialog
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [stockMovements, setStockMovements] = useState([]);
  const [historyFilter, setHistoryFilter] = useState("all"); // "all", "add", "remove"

  useEffect(() => {
    fetchInventory();
    fetchMedicines();
    fetchMedicineForms();
  }, []);

  const fetchMedicineForms = async () => {
    try {
      const res = await apiClient.get("/medicines/forms");
      const list = res.data || [];
      setMedicineForms(list);
      if (list.length) {
        const defaultForm = list.find((f) => /comprim/i.test(f)) || list[0];
        setManualMedicine((prev) => ({
          ...prev,
          form: prev.form || defaultForm,
        }));
      }
    } catch {
      setMedicineForms(["Comprimé", "Sirop", "Gélule", "Autre"]);
    }
  };

  const fetchInventory = async () => {
    try {
      const response = await apiClient.get("/pharmacy/inventory");
      setInventory(response.data);
    } catch (error) {
      toastApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMedicines = async () => {
    try {
      const response = await apiClient.get("/medicines?limit=200");
      setMedicines(response.data);
    } catch (error) {
      console.error("Error fetching medicines:", error);
    }
  };

  const searchMedicines = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    try {
      const response = await apiClient.get(`/medicines?search=${encodeURIComponent(query)}&limit=10`);
      setSearchResults(response.data);
    } catch (error) {
      console.error("Search error:", error);
    }
  };

  const fetchStockMovements = async () => {
    setHistoryLoading(true);
    try {
      const response = await apiClient.get("/pharmacy/inventory/stock-movements?limit=200");
      setStockMovements(response.data);
    } catch (error) {
      toastApiError(error);
      console.error("Stock movements error:", error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistoryDialog = () => {
    setShowHistoryDialog(true);
    fetchStockMovements();
  };

  const handleAddItem = async () => {
    setAddLoading(true);
    
    try {
      let medicineId;
      
      if (addMode === "manual") {
        // Create medicine first if manual mode
        if (!manualMedicine.name) {
          toast.error(t("inventory.toastEnterName"));
          setAddLoading(false);
          return;
        }
        
        const createResponse = await apiClient.post(
          "/medicines/create",
          {
            name: manualMedicine.name,
            brand: manualMedicine.brand || manualMedicine.name,
            molecule: manualMedicine.molecule,
            dosage: manualMedicine.dosage,
            form: manualMedicine.form,
            category: "Autre",
            requires_prescription: manualMedicine.requires_prescription
          }
        );
        
        medicineId = createResponse.data.id;
        
        if (createResponse.data.exists) {
          toast.info(t("inventory.toastAlreadyExists"));
        }
      } else {
        // Search mode - use selected medicine
        if (!selectedMedicine) {
          toast.error(t("inventory.toastSelectMedicine"));
          setAddLoading(false);
          return;
        }
        medicineId = selectedMedicine.id;
      }
      
      if (!quantity) {
        toast.error(t("inventory.toastEnterQty"));
        setAddLoading(false);
        return;
      }

      await apiClient.post(
        "/pharmacy/inventory",
        {
          medicine_id: medicineId,
          quantity: parseInt(quantity),
          price: price ? parseFloat(price) : null,
          purchase_price: purchasePrice ? parseFloat(purchasePrice) : null,
          alert_threshold: parseInt(alertThreshold),
          expiry_date: expiryDate || null
        }
      );

      toast.success(t("inventory.toastAdded"));
      setShowAddDialog(false);
      resetAddForm();
      fetchInventory();
    } catch (error) {
      toastApiError(error);
    } finally {
      setAddLoading(false);
    }
  };

  const handleUpdateItem = async () => {
    if (!editItem) return;
    
    setEditLoading(true);
    try {
      // Calculate new quantity based on action
      let newQuantity = parseInt(editQuantity);
      
      if (stockMovementQty && parseInt(stockMovementQty) > 0) {
        const movementQty = parseInt(stockMovementQty);
        if (stockAction === "add") {
          newQuantity = parseInt(editQuantity) + movementQty;
        } else {
          newQuantity = parseInt(editQuantity) - movementQty;
          if (newQuantity < 0) {
            toast.error(t("inventory.toastInsufficientQty"));
            setEditLoading(false);
            return;
          }
        }
      }
      
      // Update inventory item (quantity & price)
      await apiClient.put(
        `/pharmacy/inventory/${editItem.id}`,
        {
          quantity: newQuantity,
          price: editPrice ? parseFloat(editPrice) : null,
          purchase_price: editPurchasePrice ? parseFloat(editPurchasePrice) : null,
          stock_movement: stockMovementQty ? {
            action: stockAction,
            quantity: parseInt(stockMovementQty),
            date: stockMovementDate,
            note: stockMovementNote || null,
            purchase_price: editPurchasePrice ? parseFloat(editPurchasePrice) : null,
            expiry_date: stockAction === "add" ? (stockMovementExpiry || null) : null
          } : null
        }
      );

      // If brand was changed, update the medicine
      if (editBrand && editBrand !== editItem.medicine?.brand) {
        try {
          await apiClient.put(
            `/medicines/${editItem.medicine_id}`,
            {
              name: editItem.medicine?.name,
              brand: editBrand,
              molecule: editItem.medicine?.molecule,
              dosage: editItem.medicine?.dosage,
              form: editItem.medicine?.form
            }
          );
        } catch (error) {
          // Silently fail if user can't edit medicine (not creator)
          console.log("Could not update medicine brand");
        }
      }

      const actionText = stockMovementQty 
        ? (stockAction === "add" ? t("inventory.stockAdded", { qty: stockMovementQty }) : t("inventory.stockRemoved", { qty: stockMovementQty }))
        : t("inventory.stockUpdated");
      toast.success(actionText);
      setShowEditDialog(false);
      resetEditForm();
      fetchInventory();
    } catch (error) {
      toastApiError(error);
    } finally {
      setEditLoading(false);
    }
  };

  const resetEditForm = () => {
    setStockMovementQty("");
    setStockMovementDate(new Date().toISOString().split('T')[0]);
    setStockMovementNote("");
    setStockAction("add");
  };

  const openEditDialog = (item) => {
    setEditItem(item);
    setEditQuantity(item.quantity.toString());
    setEditPrice(item.price?.toString() || "");
    setEditPurchasePrice(item.purchase_price?.toString() || "");
    setEditBrand(item.medicine?.brand || "");
    resetEditForm();
    setShowEditDialog(true);
  };

  const handleDeleteItem = async (itemId) => {
    if (!confirm(t("inventory.toastConfirmDelete"))) return;

    try {
      await apiClient.delete(`/pharmacy/inventory/${itemId}`);
      toast.success(t("inventory.toastDeleted"));
      fetchInventory();
    } catch (error) {
      toastApiError(error);
    }
  };

  const handleOCRUpload = async (file) => {
    if (!file) return;

    setOCRLoading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await apiClient.post("/ocr/analyze", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (response.data.medicines && response.data.medicines.length > 0) {
        setOCRResults(response.data.medicines);
        toast.success(t("inventory.toastOcrDetected", { count: response.data.medicines.length }));
      } else {
        toast.info(t("inventory.toastOcrNone"));
      }
    } catch (error) {
      toastApiError(error);
    } finally {
      setOCRLoading(false);
    }
  };

  const addFromOCR = (medicine) => {
    if (medicine.matched) {
      setSelectedMedicine(medicine.matched);
      // Auto-fill expiry date from OCR if available
      if (medicine.detected.expiry_date) {
        setExpiryDate(medicine.detected.expiry_date);
      }
      setShowOCRDialog(false);
      setShowAddDialog(true);
    } else {
      // No match in DB: auto-fill the manual add form with OCR data
      const detected = medicine.detected;
      setManualMedicine({
        name: detected.name || "",
        brand: detected.name || "",
        molecule: detected.molecule || "",
        dosage: detected.dosage || "",
        form: detected.form || "comprimé"
      });
      if (detected.expiry_date) {
        setExpiryDate(detected.expiry_date);
      }
      setAddMode("manual");
      setShowOCRDialog(false);
      setShowAddDialog(true);
      toast.info(t("inventory.toastOcrPrefill"));
    }
  };

  const resetAddForm = () => {
    setSelectedMedicine(null);
    setQuantity("");
    setPrice("");
    setPurchasePrice("");
    setAlertThreshold("10");
    setExpiryDate("");
    setMedicineSearch("");
    setSearchResults([]);
    setAddMode("search");
    setManualMedicine({
      name: "",
      brand: "",
      molecule: "",
      dosage: "",
      form: "comprimé"
    });
  };

  // Filter inventory
  const filteredInventory = inventory.filter((item) => {
    const matchesSearch =
      item.medicine?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.medicine?.molecule?.toLowerCase().includes(searchTerm.toLowerCase());

    if (filter === "all") return matchesSearch;
    if (filter === "low") return matchesSearch && item.quantity <= item.alert_threshold;
    if (filter === "out") return matchesSearch && item.quantity === 0;
    return matchesSearch;
  });

  return (
    <DashboardLayout title={t("inventory.title")}>
      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            type="text"
            placeholder={t("inventory.searchPlaceholder")}
            className="pl-10 h-11 rounded-xl"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="inventory-search-input"
          />
        </div>

        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-full sm:w-48 h-11 rounded-xl" data-testid="inventory-filter">
            <SelectValue placeholder={t("inventory.filterPlaceholder")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("inventory.filterAll")}</SelectItem>
            <SelectItem value="low">{t("inventory.filterLow")}</SelectItem>
            <SelectItem value="out">{t("inventory.filterOut")}</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex gap-2">
          <Button
            variant="outline"
            className="h-11 rounded-xl"
            onClick={openHistoryDialog}
            data-testid="history-btn"
          >
            <History className="h-4 w-4 mr-2" />
            {t("inventory.history")}
          </Button>
          <Button
            variant="outline"
            className="h-11 rounded-xl"
            onClick={() => setShowOCRDialog(true)}
            data-testid="ocr-btn"
          >
            <Camera className="h-4 w-4 mr-2" />
            OCR
          </Button>
          <Button
            className="h-11 rounded-xl bg-omu-blue-500 hover:bg-omu-blue-600"
            onClick={() => setShowAddDialog(true)}
            data-testid="add-product-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t("actions.add")}
          </Button>
        </div>
      </div>

      {/* Inventory List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-10 w-10 animate-spin text-omu-blue-500" />
        </div>
      ) : filteredInventory.length === 0 ? (
        <Card className="border-0 shadow-soft">
          <CardContent className="py-12 text-center">
            <Package className="h-12 w-12 mx-auto text-slate-300" />
            <h3 className="font-heading text-lg font-semibold text-slate-900 mt-4">
              {t("inventory.emptyTitle")}
            </h3>
            <p className="text-slate-500 mt-2">
              {t("inventory.emptyDesc")}
            </p>
            <Button
              className="mt-4 rounded-full bg-omu-blue-500"
              onClick={() => setShowAddDialog(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("actions.add")} un produit
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredInventory.map((item) => (
            <Card
              key={item.id}
              className={`border-0 shadow-soft ${
                item.quantity <= item.alert_threshold ? "low-stock" : ""
              }`}
              data-testid={`inventory-item-${item.id}`}
            >
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-medium text-slate-900 truncate">
                        {item.medicine?.name}
                      </h3>
                      {item.medicine?.requires_prescription && (
                        <Badge className="bg-red-100 text-red-700 text-xs whitespace-nowrap">
                          <FileText className="h-3 w-3 mr-1" />
                          {t("inventory.prescription")}
                        </Badge>
                      )}
                      {item.quantity <= item.alert_threshold && (
                        <Badge className="bg-amber-100 text-amber-700 text-xs whitespace-nowrap">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          {t("dashboard.lowStock")}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-slate-500 mt-1 truncate">
                      {item.medicine?.dosage} • {item.medicine?.form}
                      {item.medicine?.molecule && ` • ${item.medicine.molecule}`}
                    </p>
                    {item.expiry_date && (
                      <p className="text-xs text-slate-400 mt-0.5">
                        {t("inventory.exp", { date: new Date(item.expiry_date).toLocaleDateString(dateLocale) })}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-4">
                    <div className="text-left sm:text-right">
                      <Badge
                        className={`text-xs ${
                          item.quantity > item.alert_threshold
                            ? "bg-green-100 text-green-700"
                            : item.quantity > 0
                            ? "bg-amber-100 text-amber-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {t("inventory.units", { count: item.quantity })}
                      </Badge>
                      {item.price && (
                        <p className="text-xs sm:text-sm text-omu-blue-500 font-medium mt-1">
                          {item.price.toLocaleString()} CDF
                        </p>
                      )}
                    </div>

                    <div className="flex gap-1 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 sm:h-9 sm:w-9 text-slate-500"
                        onClick={() => toggleBatches(item.id)}
                        title={t("inventory.viewBatches")}
                        data-testid={`batches-btn-${item.id}`}
                      >
                        <Package className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 sm:h-9 sm:w-9"
                        onClick={() => openEditDialog(item)}
                        data-testid={`edit-btn-${item.id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 sm:h-9 sm:w-9 text-red-500 hover:text-red-600"
                        onClick={() => handleDeleteItem(item.id)}
                        data-testid={`delete-btn-${item.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Batches / Lots */}
                {batches[item.id] && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <p className="text-xs font-semibold text-slate-600 mb-2">
                      {t("inventory.batches", { count: batches[item.id].length })}
                    </p>
                    {batches[item.id].length === 0 ? (
                      <p className="text-xs text-slate-400">{t("inventory.noBatches")}</p>
                    ) : (
                      <div className="space-y-1.5">
                        {batches[item.id].map((batch, bi) => (
                          <div key={batch.id} className="flex items-center justify-between text-xs bg-slate-50 rounded-lg px-3 py-2">
                            <div>
                              <span className="font-medium">{t("inventory.batch", { num: bi + 1 })}</span>
                              <span className="text-slate-400 ml-2">
                                {t("inventory.addedOn", { date: new Date(batch.created_at).toLocaleDateString(dateLocale) })}
                              </span>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="font-medium">{t("inventory.units", { count: batch.quantity })}</span>
                              {batch.expiry_date ? (
                                <Badge className={`text-xs ${
                                  new Date(batch.expiry_date) < new Date() 
                                    ? "bg-red-100 text-red-700" 
                                    : new Date(batch.expiry_date) < new Date(Date.now() + 30*24*60*60*1000) 
                                    ? "bg-amber-100 text-amber-700"
                                    : "bg-green-100 text-green-700"
                                }`}>
                                  Exp: {new Date(batch.expiry_date).toLocaleDateString(dateLocale)}
                                </Badge>
                              ) : (
                                <Badge className="bg-slate-200 text-slate-500 text-xs">{t("inventory.noDate")}</Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {loadingBatches[item.id] && (
                  <div className="mt-3 pt-3 border-t border-slate-100 text-center">
                    <Loader2 className="h-4 w-4 animate-spin mx-auto text-slate-400" />
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-lg max-h-[min(90dvh,calc(100vh-2rem))] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
            <DialogTitle className="font-heading">{t("actions.add")} un produit</DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto overscroll-y-contain px-6 py-2 space-y-4 min-h-0">
            {/* Mode Toggle */}
            <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
              <Button
                variant={addMode === "search" ? "default" : "ghost"}
                className={`flex-1 rounded-lg ${addMode === "search" ? "bg-omu-blue-500" : ""}`}
                onClick={() => setAddMode("search")}
              >
                {t("inventory.searchExisting")}
              </Button>
              <Button
                variant={addMode === "manual" ? "default" : "ghost"}
                className={`flex-1 rounded-lg ${addMode === "manual" ? "bg-omu-blue-500" : ""}`}
                onClick={() => setAddMode("manual")}
              >
                {t("actions.add")} manuellement
              </Button>
            </div>

            {addMode === "search" ? (
              /* Search Mode */
              <div className="space-y-2">
                <Label>{t("inventory.medicine")}</Label>
                {selectedMedicine ? (
                  <div className="flex items-center justify-between p-3 bg-omu-blue-50 rounded-xl">
                    <div>
                      <p className="font-medium">{selectedMedicine.name}</p>
                      <p className="text-sm text-slate-500">
                        {selectedMedicine.brand && selectedMedicine.brand !== selectedMedicine.name && `${selectedMedicine.brand} • `}
                        {selectedMedicine.dosage} • {selectedMedicine.form}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setSelectedMedicine(null)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder={t("inventory.searchMedicine")}
                      className="h-11 rounded-xl"
                      value={medicineSearch}
                      onChange={(e) => {
                        setMedicineSearch(e.target.value);
                        searchMedicines(e.target.value);
                      }}
                      data-testid="medicine-search-input"
                    />
                    {searchResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white rounded-xl shadow-lg border border-slate-200 max-h-60 overflow-auto">
                        {searchResults.map((med) => (
                          <div
                            key={med.id}
                            className="p-3 hover:bg-slate-50 cursor-pointer"
                            onClick={() => {
                              setSelectedMedicine(med);
                              setMedicineSearch("");
                              setSearchResults([]);
                            }}
                          >
                            <p className="font-medium">{med.name}</p>
                            <p className="text-sm text-slate-500">
                              {med.brand && med.brand !== med.name && `${med.brand} • `}
                              {med.dosage} • {med.form}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              /* Manual Mode */
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>{t("inventory.tradeName")}</Label>
                  <Input
                    type="text"
                    placeholder="Ex: Pedifen"
                    className="h-11 rounded-xl"
                    value={manualMedicine.name}
                    onChange={(e) => setManualMedicine(prev => ({ ...prev, name: e.target.value }))}
                    data-testid="manual-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("inventory.dci")}</Label>
                  <Input
                    type="text"
                    placeholder="Ex: Ibuprofène"
                    className="h-11 rounded-xl"
                    value={manualMedicine.molecule}
                    onChange={(e) => setManualMedicine(prev => ({ ...prev, molecule: e.target.value }))}
                    data-testid="manual-molecule-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>{t("inventory.dosage")}</Label>
                    <Input
                      type="text"
                      placeholder="Ex: 100mg/5mL"
                      className="h-11 rounded-xl"
                      value={manualMedicine.dosage}
                      onChange={(e) => setManualMedicine(prev => ({ ...prev, dosage: e.target.value }))}
                      data-testid="manual-dosage-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t("inventory.form")}</Label>
                    <Select 
                      value={manualMedicine.form || undefined} 
                      onValueChange={(v) => setManualMedicine(prev => ({ ...prev, form: v }))}
                    >
                      <SelectTrigger className="h-11 rounded-xl">
                        <SelectValue placeholder={t("inventory.selectForm")} />
                      </SelectTrigger>
                      <SelectContent>
                        {medicineForms.map((form) => (
                          <SelectItem key={form} value={form}>{form}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Requires Prescription */}
                <div className="flex items-center space-x-2 pt-2">
                  <input
                    type="checkbox"
                    id="requires-prescription"
                    checked={manualMedicine.requires_prescription}
                    onChange={(e) => setManualMedicine(prev => ({ ...prev, requires_prescription: e.target.checked }))}
                    className="h-4 w-4 text-omu-blue-600 border-gray-300 rounded focus:ring-omu-blue-500"
                    data-testid="prescription-checkbox"
                  />
                  <Label htmlFor="requires-prescription" className="text-sm font-medium cursor-pointer">
                    {t("inventory.requiresPrescription")}
                  </Label>
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="space-y-2">
              <Label>{t("inventory.quantity")}</Label>
              <Input
                type="number"
                placeholder="0"
                className="h-11 rounded-xl"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                data-testid="quantity-input"
              />
            </div>

            {/* Price */}
            <div className="space-y-2">
              <Label>{t("inventory.salePrice")}</Label>
              <Input
                type="number"
                placeholder={t("inventory.salePricePlaceholder")}
                className="h-11 rounded-xl"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                data-testid="price-input"
              />
            </div>

            {/* Purchase Price */}
            <div className="space-y-2">
              <Label>{t("inventory.purchasePrice")}</Label>
              <Input
                type="number"
                placeholder={t("inventory.purchasePricePlaceholder")}
                className="h-11 rounded-xl"
                value={purchasePrice}
                onChange={(e) => setPurchasePrice(e.target.value)}
                data-testid="purchase-price-input"
              />
              <p className="text-xs text-slate-500">{t("inventory.purchasePriceHint")}</p>
            </div>

            {/* Alert Threshold */}
            <div className="space-y-2">
              <Label>{t("inventory.alertThreshold")}</Label>
              <Input
                type="number"
                placeholder="10"
                className="h-11 rounded-xl"
                value={alertThreshold}
                onChange={(e) => setAlertThreshold(e.target.value)}
                data-testid="threshold-input"
              />
            </div>

            {/* Expiry Date */}
            <div className="space-y-2">
              <Label>{t("inventory.expiryDate")}</Label>
              <Input
                type="date"
                className="h-11 rounded-xl"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                data-testid="expiry-input"
              />
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t bg-white flex-shrink-0">
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              {t("actions.cancel")}
            </Button>
            <Button
              className="bg-omu-blue-500 hover:bg-omu-blue-600"
              onClick={handleAddItem}
              disabled={addLoading}
              data-testid="add-submit-btn"
            >
              {addLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("actions.add")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-lg max-h-[min(90dvh,calc(100vh-2rem))] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
            <DialogTitle className="font-heading">{t("inventory.editDialogTitle")}</DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto overscroll-y-contain px-6 py-2 space-y-4 min-h-0">
            {editItem && (
              <div className="p-3 bg-slate-50 rounded-xl">
                <p className="font-medium">{editItem.medicine?.name}</p>
                <p className="text-sm text-slate-500">
                  {editItem.medicine?.dosage} • {editItem.medicine?.form}
                </p>
                <p className="text-lg font-bold text-omu-blue-600 mt-1">
                  {t("inventory.currentStock", { count: editQuantity })}
                </p>
              </div>
            )}

            {/* Stock Movement Section */}
            <div className="border rounded-xl p-4 space-y-3">
              <Label className="text-base font-semibold">{t("inventory.stockMovement")}</Label>
              
              {/* Action Tabs */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={stockAction === "add" ? "default" : "outline"}
                  className={`flex-1 ${stockAction === "add" ? "bg-green-500 hover:bg-green-600" : ""}`}
                  onClick={() => setStockAction("add")}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t("actions.add")}
                </Button>
                <Button
                  type="button"
                  variant={stockAction === "remove" ? "default" : "outline"}
                  className={`flex-1 ${stockAction === "remove" ? "bg-red-500 hover:bg-red-600" : ""}`}
                  onClick={() => setStockAction("remove")}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t("actions.remove")}
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-sm">{t("inventory.movementQty")}</Label>
                  <Input
                    type="number"
                    min="0"
                    className="h-10"
                    placeholder="0"
                    value={stockMovementQty}
                    onChange={(e) => setStockMovementQty(e.target.value)}
                    data-testid="stock-movement-qty"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">{t("inventory.movementDate")}</Label>
                  <Input
                    type="date"
                    className="h-10"
                    value={stockMovementDate}
                    onChange={(e) => setStockMovementDate(e.target.value)}
                    data-testid="stock-movement-date"
                  />
                </div>
              </div>

              {stockAction === "add" && (
                <div className="space-y-1">
                  <Label className="text-sm">{t("inventory.newBatchExpiry")}</Label>
                  <Input
                    type="date"
                    className="h-10"
                    value={stockMovementExpiry}
                    onChange={(e) => setStockMovementExpiry(e.target.value)}
                    data-testid="stock-movement-expiry"
                  />
                  <p className="text-xs text-slate-400">{t("inventory.batchExpiryHint")}</p>
                </div>
              )}

              <div className="space-y-1">
                <Label className="text-sm">{t("inventory.noteOptional")}</Label>
                <Input
                  type="text"
                  className="h-10"
                  placeholder={t("inventory.notePlaceholder")}
                  value={stockMovementNote}
                  onChange={(e) => setStockMovementNote(e.target.value)}
                  data-testid="stock-movement-note"
                />
              </div>

              {stockMovementQty && parseInt(stockMovementQty) > 0 && (
                <div className={`p-2 rounded-lg text-sm ${stockAction === "add" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                  {stockAction === "add" 
                    ? t("inventory.newStockAdd", { total: parseInt(editQuantity) + parseInt(stockMovementQty), qty: stockMovementQty })
                    : t("inventory.newStockRemove", { total: parseInt(editQuantity) - parseInt(stockMovementQty), qty: stockMovementQty })
                  }
                </div>
              )}
            </div>

            {/* Basic Info Section */}
            <div className="border-t pt-4 space-y-3">
              <Label className="text-base font-semibold">{t("inventory.productInfo")}</Label>
              
              <div className="space-y-2">
                <Label className="text-sm">{t("inventory.brand")}</Label>
                <Input
                  type="text"
                  className="h-10"
                  value={editBrand}
                  onChange={(e) => setEditBrand(e.target.value)}
                  placeholder={t("inventory.brandPlaceholder")}
                  data-testid="edit-brand-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-sm">{t("inventory.salePrice")}</Label>
                <Input
                  type="number"
                  className="h-10"
                  value={editPrice}
                  onChange={(e) => setEditPrice(e.target.value)}
                  data-testid="edit-price-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-sm">{t("inventory.purchasePrice")}</Label>
                <Input
                  type="number"
                  className="h-10"
                  value={editPurchasePrice}
                  onChange={(e) => setEditPurchasePrice(e.target.value)}
                  placeholder={t("inventory.purchasePriceHint")}
                  data-testid="edit-purchase-price-input"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="px-6 py-4 border-t bg-white flex-shrink-0">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              {t("actions.cancel")}
            </Button>
            <Button
              className="bg-omu-blue-500 hover:bg-omu-blue-600"
              onClick={handleUpdateItem}
              disabled={editLoading}
              data-testid="edit-submit-btn"
            >
              {editLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t("actions.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OCR Dialog */}
      <Dialog open={showOCRDialog} onOpenChange={setShowOCRDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("inventory.ocrTitle")}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {ocrResults.length === 0 ? (
              <div
                className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer hover:border-omu-blue-500 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {ocrLoading ? (
                  <div>
                    <Loader2 className="h-10 w-10 animate-spin mx-auto text-omu-blue-500" />
                    <p className="mt-2 text-slate-600">{t("inventory.ocrAnalyzing")}</p>
                  </div>
                ) : (
                  <>
                    <Camera className="h-10 w-10 mx-auto text-slate-400" />
                    <p className="mt-2 text-slate-600 font-medium">
                      {t("inventory.takePhoto")}
                    </p>
                    <p className="text-sm text-slate-400">
                      {t("inventory.ocrHint")}
                    </p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => handleOCRUpload(e.target.files[0])}
                />
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">
                  {t("inventory.ocrDetected")}
                </p>
                {ocrResults.map((result, idx) => (
                  <div
                    key={idx}
                    className={`p-3 rounded-xl cursor-pointer transition-colors ${
                      result.matched
                        ? "bg-green-50 hover:bg-green-100 border border-green-200"
                        : "bg-amber-50 hover:bg-amber-100 border border-amber-200"
                    }`}
                    onClick={() => addFromOCR(result)}
                    data-testid={`ocr-result-${idx}`}
                  >
                    <p className="font-medium">{result.detected.name}</p>
                    <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                      {result.detected.molecule && <p>DCI: {result.detected.molecule}</p>}
                      {result.detected.dosage && <p>Dosage: {result.detected.dosage}</p>}
                      {result.detected.form && <p>Forme: {result.detected.form}</p>}
                      {result.detected.expiry_date && <p>Exp: {result.detected.expiry_date}</p>}
                    </div>
                    {result.matched ? (
                      <p className="text-sm text-green-600 mt-1 font-medium">
                        {t("inventory.foundInDb")}
                      </p>
                    ) : (
                      <p className="text-sm text-amber-600 mt-1 font-medium">
                        {t("inventory.newClickToAdd")}
                      </p>
                    )}
                  </div>
                ))}
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setOCRResults([]);
                    fileInputRef.current?.click();
                  }}
                >
                  {t("inventory.newPhoto")}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Stock History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[min(85dvh,calc(100vh-2rem))] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
            <DialogTitle className="font-heading flex items-center gap-2">
              <History className="h-5 w-5 text-omu-blue-500" />
              {t("inventory.history")} des mouvements de stock
            </DialogTitle>
          </DialogHeader>

          <div className="px-6 shrink-0">
            {/* Filter */}
            <div className="flex gap-2 mb-4">
              <Button
                variant={historyFilter === "all" ? "default" : "outline"}
                size="sm"
                className={`rounded-full ${historyFilter === "all" ? "bg-omu-blue-500" : ""}`}
                onClick={() => setHistoryFilter("all")}
              >
                {t("inventory.filterAllMovements")}
              </Button>
              <Button
                variant={historyFilter === "add" ? "default" : "outline"}
                size="sm"
                className={`rounded-full ${historyFilter === "add" ? "bg-green-500" : ""}`}
                onClick={() => setHistoryFilter("add")}
              >
                <ArrowUpCircle className="h-3 w-3 mr-1" />
                {t("inventory.filterEntries")}
              </Button>
              <Button
                variant={historyFilter === "remove" ? "default" : "outline"}
                size="sm"
                className={`rounded-full ${historyFilter === "remove" ? "bg-red-500" : ""}`}
                onClick={() => setHistoryFilter("remove")}
              >
                <ArrowDownCircle className="h-3 w-3 mr-1" />
                {t("inventory.filterExits")}
              </Button>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain px-6 py-2">
            {historyLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-omu-blue-500" />
              </div>
            ) : stockMovements.length === 0 ? (
              <div className="text-center py-12">
                <History className="h-12 w-12 mx-auto text-slate-300" />
                <p className="mt-4 text-slate-500">{t("inventory.noMovements")}</p>
                <p className="text-sm text-slate-400 mt-1">
                  {t("inventory.noMovementsHint")}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {stockMovements
                  .filter(m => historyFilter === "all" || m.action === historyFilter)
                  .map((movement, idx) => (
                    <div
                      key={movement.id || idx}
                      className={`p-3 rounded-xl border ${
                        movement.action === "add" 
                          ? "bg-green-50 border-green-200" 
                          : "bg-red-50 border-red-200"
                      }`}
                      data-testid={`movement-${movement.id || idx}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          {movement.action === "add" ? (
                            <div className="p-2 rounded-full bg-green-100">
                              <ArrowUpCircle className="h-4 w-4 text-green-600" />
                            </div>
                          ) : (
                            <div className="p-2 rounded-full bg-red-100">
                              <ArrowDownCircle className="h-4 w-4 text-red-600" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-slate-900">
                              {movement.medicine_name || t("inventory.medicineFallback")}
                            </p>
                            <p className={`text-sm font-semibold ${
                              movement.action === "add" ? "text-green-600" : "text-red-600"
                            }`}>
                              {movement.action === "add" ? "+" : "-"}{t("inventory.units", { count: movement.quantity })}
                            </p>
                            {movement.note && (
                              <p className="text-xs text-slate-500 mt-1">
                                {t("inventory.note", { text: movement.note })}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="text-right text-xs text-slate-500">
                          <p>{movement.date}</p>
                          <p className="mt-1">
                            {movement.old_stock} → {movement.new_stock}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>

          <DialogFooter className="px-6 py-4 border-t shrink-0 bg-background">
            <Button variant="outline" onClick={() => setShowHistoryDialog(false)}>
              {t("actions.close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default PharmacyInventory;
